import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CalendarBlank, Plus, Trash, Clock, User, Users, WarningCircle } from "@phosphor-icons/react";
import { format } from "date-fns";

const TIME_SLOTS = [];
for (let h = 6; h <= 23; h++) {
  for (let m of [0, 30]) {
    TIME_SLOTS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

const SHIFT_COLORS = [
  "bg-blue-100 border-blue-300 text-blue-800",
  "bg-green-100 border-green-300 text-green-800",
  "bg-purple-100 border-purple-300 text-purple-800",
  "bg-orange-100 border-orange-300 text-orange-800",
  "bg-pink-100 border-pink-300 text-pink-800",
  "bg-teal-100 border-teal-300 text-teal-800",
  "bg-yellow-100 border-yellow-300 text-yellow-800",
  "bg-red-100 border-red-300 text-red-800",
];

const ShiftsPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ user_id: "", room: "", start_time: "09:00", end_time: "17:00", notes: "" });

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [shiftsRes, roomsRes, usersRes, coverageRes] = await Promise.all([
        axios.get(`${API}/shifts?date=${dateStr}`),
        axios.get(`${API}/rooms`),
        axios.get(`${API}/users`),
        axios.get(`${API}/shifts/coverage/${dateStr}`)
      ]);
      setShifts(shiftsRes.data);
      setRooms(roomsRes.data);
      setUsers(usersRes.data);
      setCoverage(coverageRes.data);
    } catch (error) {
      console.error("Failed to load shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.user_id || !form.room || !form.start_time || !form.end_time) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.start_time >= form.end_time) {
      toast.error("End time must be after start time");
      return;
    }
    try {
      await axios.post(`${API}/shifts`, { ...form, date: dateStr });
      toast.success("Shift created");
      setShowDialog(false);
      setForm({ user_id: "", room: "", start_time: "09:00", end_time: "17:00", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create shift");
    }
  };

  const handleDelete = async (shiftId) => {
    try {
      await axios.delete(`${API}/shifts/${shiftId}`);
      toast.success("Shift deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete shift");
    }
  };

  // Color assignment by user
  const userColorMap = {};
  let colorIdx = 0;
  shifts.forEach(s => {
    if (!userColorMap[s.user_id]) {
      userColorMap[s.user_id] = SHIFT_COLORS[colorIdx % SHIFT_COLORS.length];
      colorIdx++;
    }
  });

  const serverUsers = users.filter(u => ["server", "cashier"].includes(u.role));

  return (
    <div className="min-h-screen bg-background" data-testid="shifts-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Shift Schedule</h1>
            <p className="text-muted-foreground">Assign staff to rooms and manage coverage</p>
          </div>
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="shift-date-picker">
                  <CalendarBlank size={18} />
                  {format(selectedDate, "EEE, MMM d")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} initialFocus />
              </PopoverContent>
            </Popover>
            <Button className="gap-2" onClick={() => setShowDialog(true)} data-testid="add-shift-btn">
              <Plus size={18} /> Add Shift
            </Button>
          </div>
        </div>

        {/* Coverage Summary */}
        {coverage && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Clock size={24} className="text-primary" weight="fill" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Shifts</p>
                  <p className="font-mono text-2xl font-medium">{coverage.total_shifts}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users size={24} className="text-green-600" weight="fill" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Staff Scheduled</p>
                  <p className="font-mono text-2xl font-medium">{coverage.total_staff}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Clock size={24} className="text-blue-600" weight="fill" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Hours</p>
                  <p className="font-mono text-2xl font-medium">
                    {Object.values(coverage.coverage).reduce((s, r) => s + r.total_hours, 0).toFixed(1)}
                  </p>
                </div>
              </CardContent>
            </Card>
            {coverage.uncovered_rooms.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50/50">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <WarningCircle size={24} className="text-yellow-600" weight="fill" />
                  </div>
                  <div>
                    <p className="text-sm text-yellow-700 font-medium">Uncovered</p>
                    <p className="text-sm text-yellow-600">{coverage.uncovered_rooms.join(", ")}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Schedule Grid by Room */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {rooms.map(room => {
              const roomShifts = shifts.filter(s => s.room === room.name);
              const roomCov = coverage?.coverage?.[room.name];
              
              return (
                <Card key={room.id} data-testid={`room-schedule-${room.name}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-lg">{room.name}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">
                          {roomCov?.staff_count || 0} staff
                        </Badge>
                        <Badge variant="outline" className="font-mono">
                          {roomCov?.total_hours?.toFixed(1) || 0}h
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {roomShifts.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground bg-secondary/20 rounded-lg">
                        <p className="text-sm">No shifts scheduled</p>
                        <Button variant="link" size="sm" onClick={() => { setForm(f => ({ ...f, room: room.name })); setShowDialog(true); }}>
                          Add a shift
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {roomShifts.map(shift => (
                          <div
                            key={shift.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${userColorMap[shift.user_id] || "bg-secondary/30 border-border"}`}
                            data-testid={`shift-${shift.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-white/70 flex items-center justify-center">
                                <User size={18} weight="bold" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{shift.user_name}</p>
                                <p className="text-xs opacity-75">
                                  <Clock size={12} className="inline mr-1" />
                                  {shift.start_time} - {shift.end_time}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {shift.notes && (
                                <span className="text-xs opacity-60 max-w-[150px] truncate">{shift.notes}</span>
                              )}
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-current opacity-50 hover:opacity-100" onClick={() => handleDelete(shift.id)} data-testid={`delete-shift-${shift.id}`}>
                                <Trash size={14} />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Add Shift Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[440px]" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Staff Member *</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm(f => ({ ...f, user_id: v }))}>
                <SelectTrigger data-testid="shift-user"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Room *</Label>
              <Select value={form.room} onValueChange={(v) => setForm(f => ({ ...f, room: v }))}>
                <SelectTrigger data-testid="shift-room"><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>
                  {rooms.map(r => (
                    <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Select value={form.start_time} onValueChange={(v) => setForm(f => ({ ...f, start_time: v }))}>
                  <SelectTrigger data-testid="shift-start"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Select value={form.end_time} onValueChange={(v) => setForm(f => ({ ...f, end_time: v }))}>
                  <SelectTrigger data-testid="shift-end"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.filter(t => t > form.start_time).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea placeholder="Training shift, covering for Alex, etc." value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="shift-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-shift-btn">Create Shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftsPage;
